import React, { memo, useState, useEffect } from 'react';
import BN from 'bignumber.js';
import get from 'lodash/get';
import ConfirmOrderModalContainer from './ConfirmOrderModal';
import ProductIconContainer from '@alphapoint/apex-web/lib/components/common/ProductIcon/ProductIconContainer';
import APLabelWithText from '@alphapoint/apex-web/lib/components/common/APLabelWithText';
import APNumberInput from '@alphapoint/apex-web/lib/components/common/APNumberInput';
import { RFormInputItem } from '@alphapoint/apex-web/lib/components/form/FormItem';
import { SpinNumberInput } from '@alphapoint/apex-web/lib/components/form/NumberInput';
import resize from '@alphapoint/apex-web/lib/hocs/resize';
import { formatNumberToLocale, parseNumberToLocale } from '@alphapoint/apex-web/lib/helpers/numberFormatter';
import { convertIncrementToIntDecimalPlaces } from '@alphapoint/apex-web/lib/helpers/decimalPlaces/decimalPlacesHelper';
import { getOrderFee } from '@alphapoint/apex-web/lib/helpers/recalculateOrderFeeHelper';
import { getBEMClasses } from '../../helpers/cssClassesHelper';
import './StreamingCard.css';

const streamingCardClasses = getBEMClasses('apg-streaming-card');
const TIF_GTC = 'TIF: GTC';
const TIF_FOK = 'TIF: FOK';
const TIF_IOC = 'TIF: IOC';
const TIMEINFORCE_GTC = '1';
const TIMEINFORCE_FOK = '4';
const TIMEINFORCE_IOC = '3';
const MARKET_ORDER = 'Market Order';
const LIMIT_ORDER = 'Limit Order';

const getCurrentTime = () => {
  const today = new Date().toGMTString();
  const currentTimezoneSplit = today.split(' ');
  const extractTime = `${currentTimezoneSplit[4]} UTC`;
  return extractTime;
};

const CardTimezone = memo(() => {
  const [timezone, setTimezone] = useState('');
  useEffect(() => {
    const interval = setInterval(() => {
      setTimezone(getCurrentTime());
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);
  return <span className={streamingCardClasses('small-text-span')}>{timezone}</span>;
});
CardTimezone.displayName = 'CardTimezone';

const StreamingCard = (props) => {
  const {
    defaultSelection,
    position,
    instrument,
    products,
    level1,
    selectOrderType,
    productManifest,
    AccountId,
    showNotification,
    clearForm,
    selectedInstrumentId,
    additionalSlipage,
  } = props;

  const [isConfirmModalOpen, setConfirmModal] = useState(false);
  const [form, setForm] = useState(false);
  const [metaData, setMetaData] = useState(false);
  const [orderFee, setOrderFee] = useState({ OrderFee: 0, productId: 0 });

  const dismissConfirmModal = () => {
    setConfirmModal(false);
  };

  const fetchOrderFee = (orderFeeData) => {
    getOrderFee(orderFeeData).then((result) => {
      const data = {
        ...result,
        product: products.find((prod) => prod.ProductId === result.ProductId).Product,
      };
      setOrderFee(data);
    });
  };

  const handleSubmit = (form, orderFeeData, confirmationModalProps) => {
    fetchOrderFee(orderFeeData);
    setConfirmModal(true);
    setForm(form);
    setMetaData(confirmationModalProps);
  };

  const currentLevel = get(level1, [instrument.InstrumentId], {});

  const getProductName = (product) => {
    const search = products.find((prod) => prod.Product === product);
    if (search) {
      return search.ProductFullName;
    } else {
      return product;
    }
  };

  const calculateSpread = (bestBid, bestOffer) => {
    let bBid = parseFloat(bestBid.replace(/,/g, ''));
    let bOffer = parseFloat(bestOffer.replace(/,/g, ''));

    return formatNumberToLocale(Math.abs(bBid - bOffer), convertIncrementToIntDecimalPlaces(instrument.PriceIncrement));
  };

  const fetchProductFileName = (symbol) => {
    return Object.keys(productManifest.manifest).indexOf(symbol) === -1
      ? symbol + 'DEFAULT_ICON'
      : productManifest.manifest[symbol].iconFileName;
  };

  const buyPrice =
    defaultSelection[position].newBuyPrice == ''
      ? formatNumberToLocale(currentLevel.BestOffer, convertIncrementToIntDecimalPlaces(instrument.PriceIncrement))
      : defaultSelection[position].newBuyPrice;

  const sellPrice =
    defaultSelection[position].newSellPrice == ''
      ? formatNumberToLocale(currentLevel.BestBid, convertIncrementToIntDecimalPlaces(instrument.PriceIncrement))
      : defaultSelection[position].newSellPrice;

  const limitBuyPrice =
    defaultSelection[position].limitBuyPrice == ''
      ? formatNumberToLocale(currentLevel.BestOffer, convertIncrementToIntDecimalPlaces(instrument.PriceIncrement))
      : defaultSelection[position].limitBuyPrice;

  const limitSellPrice =
    defaultSelection[position].limitSellPrice == ''
      ? formatNumberToLocale(currentLevel.BestBid, convertIncrementToIntDecimalPlaces(instrument.PriceIncrement))
      : defaultSelection[position].limitSellPrice;

  const maxValue = formatNumberToLocale(
    parseFloat(parseNumberToLocale(buyPrice)) +
      parseFloat(parseNumberToLocale(buyPrice)) *
        (parseFloat(defaultSelection[position].slipage) + additionalSlipage) *
        0.0001,
    convertIncrementToIntDecimalPlaces(instrument.PriceIncrement),
  );

  const minValue = formatNumberToLocale(
    parseFloat(parseNumberToLocale(sellPrice)) -
      parseFloat(parseNumberToLocale(sellPrice)) *
        (parseFloat(defaultSelection[position].slipage) + additionalSlipage) *
        0.0001,
    convertIncrementToIntDecimalPlaces(instrument.PriceIncrement),
  );

  const maxLimitValue = formatNumberToLocale(
    parseFloat(parseNumberToLocale(limitBuyPrice)) +
      parseFloat(parseNumberToLocale(limitBuyPrice)) *
        (parseFloat(defaultSelection[position].slipage) + additionalSlipage) *
        0.0001,
    convertIncrementToIntDecimalPlaces(instrument.PriceIncrement),
  );

  const minLimitValue = formatNumberToLocale(
    parseFloat(parseNumberToLocale(limitSellPrice)) -
      parseFloat(parseNumberToLocale(limitSellPrice)) *
        (parseFloat(defaultSelection[position].slipage) + additionalSlipage) *
        0.0001,
    convertIncrementToIntDecimalPlaces(instrument.PriceIncrement),
  );

  const buildOrderForm = (
    orderType,
    currentLevel,
    selectedInstrument,
    inputPrice,
    selectedSide,
    timeInForce,
    limitPrice,
    minmaxLimitValue,
  ) => {
    const price = inputPrice.replace(',', '');
    const quantity = defaultSelection[position].qty;
    const selectedInstrumentId = selectedInstrument.InstrumentId;
    const side = selectedSide;
    const stopPrice = '';
    const totalAmount = '0';

    const form = {
      fee: orderFee,
      limitPrice: limitPrice,
      orderType: orderType,
      quantity: quantity,
      selectedInstrumentId: selectedInstrumentId,
      side: side,
      stopPrice: stopPrice,
      timeInForce: timeInForce,
      totalAmount: totalAmount,
    };

    const orderFeeData = {
      OMSId: currentLevel.OMSId,
      AccountId: AccountId,
      InstrumentId: selectedInstrumentId,
      Amount: defaultSelection[position].qty,
      Quantity: defaultSelection[position].qty,
      Side: side,
      Price: price,
      OrderType: orderType,
      BestBid: currentLevel.BestBid,
      BestOffer: currentLevel.BestOffer,
    };

    const orderTotal = new BN(price).times(new BN(quantity));

    const confirmationModalProps = {
      marketPrice: price,
      orderTotal: formatNumberToLocale(
        orderTotal,
        convertIncrementToIntDecimalPlaces(selectedInstrument.PriceIncrement),
      ),
      Product1Symbol: selectedInstrument.Product1Symbol,
      Product2Symbol: selectedInstrument.Product2Symbol,
      isBuySide: selectedSide == '0' ? true : false,
      minmaxLimitValue: minmaxLimitValue,
    };
    handleSubmit(form, orderFeeData, confirmationModalProps);
  };

  const placeBuyOrder = (instrument, price, selectedPosition, minmaxValue, minmaxLimitValue) => {
    const timeInForce =
      defaultSelection[position].orderTypeValue == TIF_GTC
        ? TIMEINFORCE_GTC
        : defaultSelection[position].orderTypeValue == TIF_IOC
          ? TIMEINFORCE_IOC
          : TIMEINFORCE_FOK;

    const orderType = defaultSelection[position].orderTypeValue == TIF_GTC ? MARKET_ORDER : LIMIT_ORDER;

    const selectedPrice = defaultSelection[position].orderTypeValue == TIF_GTC ? price : minmaxLimitValue;

    const limitPrice = defaultSelection[position].orderTypeValue == TIF_GTC ? price : minmaxLimitValue;

    if (defaultSelection[position].qty <= 0) {
      showNotification('Please enter valid quantity');
      return;
    }

    if (defaultSelection[position].orderTypeValue != TIF_GTC && defaultSelection[position].slipage <= 0) {
      showNotification('Please enter valid slippage');
      return;
    }

    buildOrderForm(
      orderType,
      currentLevel,
      instrument,
      selectedPrice,
      0,
      timeInForce,
      selectedPosition,
      limitPrice,
      minmaxLimitValue,
    );
  };

  const placeSellOrder = (instrument, price, selectedPosition, minmaxValue, minmaxLimitValue) => {
    const timeInForce =
      defaultSelection[position].orderTypeValue == TIF_GTC
        ? TIMEINFORCE_GTC
        : defaultSelection[position].orderTypeValue == TIF_IOC
          ? TIMEINFORCE_IOC
          : TIMEINFORCE_FOK;

    const orderType = defaultSelection[position].orderTypeValue == TIF_GTC ? MARKET_ORDER : LIMIT_ORDER;

    const selectedPrice = defaultSelection[position].orderTypeValue == TIF_GTC ? price : minmaxLimitValue;

    const limitPrice = defaultSelection[position].orderTypeValue == TIF_GTC ? '' : minmaxLimitValue;

    if (defaultSelection[position].qty <= 0) {
      showNotification('Please enter valid quantity');
      return;
    }

    if (defaultSelection[position].orderTypeValue != TIF_GTC && defaultSelection[position].slipage <= 0) {
      showNotification('Please enter valid slippage');
      return;
    }

    buildOrderForm(
      orderType,
      currentLevel,
      instrument,
      selectedPrice,
      1,
      timeInForce,
      selectedPosition,
      limitPrice,
      minmaxLimitValue,
    );
  };

  const spreadValue = calculateSpread(buyPrice, sellPrice);

  return (
    <div
      className={`${
        defaultSelection[position].orderTypeValue != TIF_GTC ? streamingCardClasses('expanded') : streamingCardClasses()
      } container-with-shadow`}>
      <div className={streamingCardClasses('header-row')}>
        <div className={streamingCardClasses('product-icon')}>
          <ProductIconContainer iconFileName={fetchProductFileName(instrument.Product1Symbol)} size={32} />
        </div>
        <APLabelWithText
          label={`${getProductName(instrument.Product1Symbol)} / ${getProductName(instrument.Product2Symbol)}`}
          text={instrument.Symbol}
          customClass={streamingCardClasses()}
          classModifiers={'header'}
        />
      </div>
      <div className={streamingCardClasses('sell-buy-wrapper')}>
        <div
          className={streamingCardClasses('sell-block-text')}
          onClick={() => placeSellOrder(instrument, sellPrice, position, minValue, minLimitValue)}>
          <span className={streamingCardClasses('sell-block-title')}>Sell</span>
          <span className={streamingCardClasses('sell-block-value')}>{sellPrice}</span>
        </div>
        <div
          className={streamingCardClasses('buy-block-text')}
          onClick={() => placeBuyOrder(instrument, buyPrice, position, maxValue, maxLimitValue)}>
          <span className={streamingCardClasses('buy-block-title')}>Buy</span>
          <span className={streamingCardClasses('buy-block-value')}>{buyPrice}</span>
        </div>
        <div className={streamingCardClasses('sell-buy-top-wrapper')}>
          <div
            className={streamingCardClasses('sell-top-wrapper')}
            onClick={() => placeSellOrder(instrument, sellPrice, position, minValue, minLimitValue)}>
            <div className={streamingCardClasses('sell-top-wrapper-triangle')} />
          </div>
          <div className={streamingCardClasses('spread-wrapper')}>
            <div className={streamingCardClasses('spread-wrapper-text')}>{spreadValue}</div>
          </div>
          <div
            className={streamingCardClasses('buy-top-wrapper')}
            onClick={() => placeBuyOrder(instrument, buyPrice, position, maxValue, maxLimitValue)}>
            <div className={streamingCardClasses('buy-top-wrapper-triangle')} />
          </div>
        </div>
        <div className={streamingCardClasses('sell-buy-bottom-wrapper')}>
          <div
            className={streamingCardClasses('sell-bottom-wrapper')}
            onClick={() => placeSellOrder(instrument, sellPrice, position, minValue, minLimitValue)}
          />
          <div
            className={streamingCardClasses('buy-bottom-wrapper')}
            onClick={() => placeBuyOrder(instrument, buyPrice, position, maxValue, maxLimitValue)}
          />
        </div>
      </div>
      <div className={streamingCardClasses('footer-rows')}>
        <RFormInputItem
          component={SpinNumberInput}
          size="small"
          valueAs="string"
          placeholder="0"
          name={`quantity-${position}`}
          decimalPlaces={convertIncrementToIntDecimalPlaces(instrument.QuantityIncrement)}
          step={instrument.QuantityIncrement}
          min={0}
          max={Infinity}
          classModifiers={'quantity'}
          customClass={streamingCardClasses()}
        />
        <div className={streamingCardClasses('timezone')}>
          <div>
            <CardTimezone />
          </div>
          <div>
            <select
              value={defaultSelection[position].orderTypeValue}
              onChange={(value) => selectOrderType(position, value)}
              className={streamingCardClasses('ap-instrument-type-select')}>
              <option value={TIF_GTC}>{TIF_GTC}</option>
              <option value={TIF_FOK}>{TIF_FOK}</option>
              <option value={TIF_IOC}>{TIF_IOC}</option>
            </select>
          </div>
        </div>
        {defaultSelection[position].orderTypeValue != TIF_GTC && (
          <div>
            <div className={streamingCardClasses('row')}>
              <div className={streamingCardClasses('slip-page-text')}>
                <span className={streamingCardClasses('small-text-span')}>Allowed slippage (bps)</span>
              </div>
              <APNumberInput
                type="text"
                placeholder="10"
                name={`slipage-${position}`}
                classModifiers={'slippage'}
                customClass={streamingCardClasses()}
                decimalPlaces={convertIncrementToIntDecimalPlaces(instrument.PriceIncrement)}
                step={instrument.PriceIncrement}
              />
            </div>

            <div className={streamingCardClasses('min-max')}>
              <div>
                <span className={streamingCardClasses('small-text-span')}>
                  Min: <font className={streamingCardClasses('footer-text-span-min')}>{minLimitValue}</font>
                </span>
              </div>
              <div>
                <span className={streamingCardClasses('small-text-span')}>
                  Max: <font className={streamingCardClasses('footer-text-span-max')}>{maxLimitValue}</font>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      <ConfirmOrderModalContainer
        isConfirmModalOpen={isConfirmModalOpen}
        formData={form}
        dismissConfirmModal={dismissConfirmModal}
        orderFee={orderFee}
        metaData={metaData}
        priceIncrement={instrument.PriceIncrement}
        quantityIncrement={instrument.QuantityIncrement}
        clearForm={clearForm}
        selectedInstrumentId={selectedInstrumentId}
      />
    </div>
  );
};

export default resize(StreamingCard);
